import { FeeBreakdown, List, Money, RateBreakdown, UiFee } from '@razz-team/ui-web';
import { ApiTransaction, ApiTransactionPart } from '../../../openapi';
import { useMemo } from 'react';
import { BreakdownListItem } from '@razz-team/ui-web/src/components/List/BreakdownListItem';
import { useTranslation } from 'react-i18next';
import { isMoneyEquals } from '@razz-team/utils';

function StartAmountLabel({ trx }: { trx: ApiTransaction }) {
  const { t } = useTranslation();

  if (trx.direction === 'IN') {
    switch (trx.type) {
      case 'OWN_TRANSFER_IN':
        return t('Amount to exchange');
      default:
        return t('Top-up amount');
    }
  } else {
    switch (trx.type) {
      default:
        return t('Debited amount');
    }
  }
}

function EndAmountLabel({ trx }: { trx: ApiTransaction }) {
  const { t } = useTranslation();

  if (trx.direction === 'IN') {
    switch (trx.type) {
      case 'OWN_TRANSFER_IN':
        return t('Exchanged amount');
      default:
        return t('Top-up amount');
    }
  } else {
    switch (trx.type) {
      default:
        return t('Payment amount');
    }
  }
}

function mapFeeToUiFee(fees: ApiTransactionPart['fees']): UiFee[] {
  return fees
    ? Object.entries(fees).reduce((acc, [type, fee]) => {
        if (fee?.sourceAmount) {
          acc.push({
            type,
            value: fee.sourceAmount,
          });
        }
        return acc;
      }, [] as UiFee[])
    : [];
}

export function TransactionBreakdown({ trx }: { trx: ApiTransaction }) {
  const originFees = useMemo(() => mapFeeToUiFee(trx.origin.fees), [trx.origin.fees]);
  const cpartyFees = useMemo(() => mapFeeToUiFee(trx.counterparty.fees), [trx.counterparty.fees]);

  const uiRate = useMemo(() => {
    const fx = trx?.fx;
    if (fx) {
      return {
        price: fx.side === 'BUY' ? fx.rate.ask : fx.rate.bid,
        currencyPair: fx.rate.currencyPair,
      };
    }

    return undefined;
  }, [trx]);

  const listItems = [
    <BreakdownListItem
      key="topUpAmount"
      loading={false}
      variant="confirm"
      icon="dot"
      title={<StartAmountLabel trx={trx} />}
      value={<Money value={trx.origin.amount} />}
    />,
  ];

  if (!trx.type.includes('FEE')) {
    listItems.push(
      <FeeBreakdown
        key="originFees"
        variant="confirm"
        fees={originFees}
        side={trx.direction === 'OUT' ? 'SELL' : trx.fx?.side}
      />,
    );
  }

  if (uiRate) {
    listItems.push(<RateBreakdown key="rate" variant="confirm" rate={uiRate} />);
  }

  if (cpartyFees.length > 0) {
    listItems.push(
      <FeeBreakdown
        key="cpartyFees"
        variant="confirm"
        fees={cpartyFees}
        side={trx.direction === 'OUT' ? 'SELL' : trx.fx?.side}
      />,
    );
  }

  if (!isMoneyEquals(trx.origin.amount, trx.counterparty.amount)) {
    listItems.push(
      <BreakdownListItem
        key="paymentAmount"
        loading={false}
        variant="confirm"
        icon="equal"
        title={<EndAmountLabel trx={trx} />}
        value={<Money value={trx.counterparty.amount} />}
      />,
    );
  }

  return <List>{listItems}</List>;
}
